/**
 * BlockCard — one card per block on the site Alarms tab.
 *
 * The device sub-header renders ONLY when the block holds more than one
 * device, so a generator block (where block == device) reads exactly like the
 * portfolio hub's flat card, while a BESS block visibly separates its pack
 * from its BMS.
 *
 * Pane-of-glass: rows are navigation only — clicking opens the read-only
 * slide-over. No ack / clear / resolve controls here or anywhere below.
 */

import { cn } from '@/utils/cn';
import { AlarmRow, toDisplayRow } from '../primitives';
import { conditionKeyLabel } from './condition-key';
import type { BlockGroup } from './alarm-grouping';
import type { ActiveAlarm } from '@/types/alarm.types';

const EDGE_BY_SEVERITY = {
  critical: 'border-l-ems-red',
  warning: 'border-l-ems-amber',
  info: 'border-l-ems-blue',
} as const;

interface Props {
  group: BlockGroup;
  onAlarmClick: (alarm: ActiveAlarm) => void;
}

export function BlockCard({ group, onAlarmClick }: Props) {
  const showDeviceHeaders = group.devices.length > 1;

  return (
    <div
      data-testid="block-card"
      data-block-id={group.blockId ?? ''}
      className={cn(
        'border border-ems-border border-l-4 rounded overflow-hidden',
        group.highestSeverity ? EDGE_BY_SEVERITY[group.highestSeverity] : 'border-l-ems-green',
      )}
    >
      <div
        data-testid="block-card-header"
        className="flex items-center justify-between gap-2 px-3 py-2 bg-ems-bg-card border-b border-ems-border"
      >
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-sm font-semibold text-ems-text-primary truncate">
            {group.label}
          </span>
          <span className="text-xs text-ems-text-muted whitespace-nowrap">
            · {group.totalRows} active
          </span>
        </div>
        <div className="flex gap-1 shrink-0">
          {group.counts.critical > 0 && (
            <span
              data-testid="block-count-critical"
              className="bg-ems-red/20 text-ems-red px-2 py-0.5 rounded-full text-[10px] font-semibold"
            >
              {group.counts.critical}
            </span>
          )}
          {group.counts.warning > 0 && (
            <span
              data-testid="block-count-warning"
              className="bg-ems-amber/20 text-ems-amber px-2 py-0.5 rounded-full text-[10px] font-semibold"
            >
              {group.counts.warning}
            </span>
          )}
          {group.counts.info > 0 && (
            <span
              data-testid="block-count-info"
              className="bg-ems-blue/20 text-ems-blue px-2 py-0.5 rounded-full text-[10px] font-semibold"
            >
              {group.counts.info}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5 p-2">
        {group.devices.map((device) => (
          <div key={device.deviceId} className="flex flex-col gap-1.5">
            {showDeviceHeaders && (
              <div
                data-testid="device-subheader"
                className="flex items-baseline gap-2 px-1 pt-1 text-[10px] uppercase tracking-wider text-ems-text-muted"
              >
                <span className="text-ems-text-secondary font-semibold">{device.deviceId}</span>
                <span>· {device.deviceType}</span>
                <span className="ml-auto normal-case tracking-normal">
                  ({device.rows.length})
                </span>
              </div>
            )}
            {device.rows.map((row) => (
              <AlarmRow
                key={`${row.device_id}:${row.condition_key}`}
                alarm={toDisplayRow(row)}
                subLabel={conditionKeyLabel(row.condition_key)}
                onClick={() => onAlarmClick(row)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
